/**
 * Regioes vivas — DESIGN_SYSTEM §3.5.
 *
 * Existem **exatamente tres**, montadas uma vez na raiz. Componente **nao cria** regiao viva
 * propria: publica por `useAnnounce()`. Regiao viva criada dentro de um componente que monta e
 * desmonta e a razao classica de anuncio que nunca sai — o leitor precisa da regiao ja no DOM
 * antes de o texto entrar nela.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { t } from '../content';

/**
 * §3.5:
 *   status : aria-live="polite"    aria-atomic="true"  — carrinho, filtro, resultados
 *   alert  : aria-live="assertive" role="alert"        — falha de pagamento, sessao expirada, perda de conexao
 *   log    : aria-live="polite"    role="log"          — fila do Painel da Loja (novo pedido)
 */
export type LiveRegion = 'status' | 'alert' | 'log';

export interface Announcer {
  /**
   * `assertive` e reservado a TRES casos (§3.5): falha de pagamento, expiracao de sessao/Pix e
   * perda de conexao durante checkout. Qualquer outro uso exige revisao.
   */
  readonly anunciar: (mensagem: string, regiao?: LiveRegion) => void;
}

const ContextoDeAnuncio = createContext<Announcer | null>(null);

type Mensagens = Readonly<Record<LiveRegion, string>>;

const VAZIO: Mensagens = { status: '', alert: '', log: '' };

export interface LiveRegionsProps {
  readonly children?: ReactNode;
}

/** Monte uma unica vez, na raiz do app. */
export function LiveRegions({ children }: LiveRegionsProps): ReactNode {
  const [mensagens, setMensagens] = useState<Mensagens>(VAZIO);

  const anunciar = useCallback((mensagem: string, regiao: LiveRegion = 'status') => {
    // Reescrever com o MESMO texto nao dispara o leitor. O ciclo por string vazia garante que
    // "1 no carrinho" duas vezes seguidas seja anunciado duas vezes.
    setMensagens((atual) => ({ ...atual, [regiao]: '' }));
    setMensagens((atual) => ({ ...atual, [regiao]: mensagem }));
  }, []);

  const valor = useMemo<Announcer>(() => ({ anunciar }), [anunciar]);

  return (
    <ContextoDeAnuncio.Provider value={valor}>
      {children}
      {/* `role="status"` e obrigatorio, e nao decorativo: `aria-label` num <div> sem papel e
          proibido pela ARIA (regra `aria-prohibited-attr`) e o rotulo simplesmente nao chega ao
          leitor. O papel implica `aria-live="polite"`; o atributo fica explicito porque a §3.5
          o declara. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        aria-label={t('a11y.regiaoStatus')}
        className="balcao-sr-only"
      >
        {mensagens.status}
      </div>
      <div role="alert" aria-live="assertive" aria-label={t('a11y.regiaoAlerta')} className="balcao-sr-only">
        {mensagens.alert}
      </div>
      <div role="log" aria-live="polite" aria-label={t('a11y.regiaoLog')} className="balcao-sr-only">
        {mensagens.log}
      </div>
    </ContextoDeAnuncio.Provider>
  );
}

/**
 * Publica numa das tres regioes. Fora de `<LiveRegions>` vira no-op silencioso e um aviso em
 * desenvolvimento: um componente de biblioteca nao pode derrubar o app do cliente porque a
 * raiz esqueceu de montar a regiao — mas tambem nao pode deixar o esquecimento invisivel.
 */
export function useAnnounce(): Announcer {
  const contexto = useContext(ContextoDeAnuncio);
  const semContexto = contexto === null;

  useEffect(() => {
    if (semContexto && process.env.NODE_ENV !== 'production') {
      console.warn(
        'useAnnounce fora de <LiveRegions>: o anuncio nao vai ao leitor de tela. ' +
          'DESIGN_SYSTEM §3.5 — as tres regioes vivas sao montadas uma vez na raiz.',
      );
    }
  }, [semContexto]);

  return useMemo<Announcer>(
    () => contexto ?? { anunciar: () => undefined },
    [contexto],
  );
}
